import { FieldError } from "react-hook-form";
import { Link, styled } from "@mui/material";
import { FaPenToSquare, FaRegCirclePlay, FaRegTrashCan } from "react-icons/fa6";

import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";

export const ModalContainer = () => ({
  position: "relative",
  width: "32%",
  backgroundColor: "background.paper",
  padding: "1.2rem",
  border: `0.15rem solid ${colors.grey[1]}`,
  borderRadius: "0.25rem",
  maxHeight: '75vh',
  overflowY: 'auto',
});

export const VideoCardStyle = () => ({
  maxWidth: "100%",
  backgroundColor: colors.white[1],
  border: `0.0625rem solid ${colors.portalColor[27]}`,
  borderRadius: "0.6rem",
  boxShadow: `0 0.125rem 0.25rem ${colors.portalColor[28]}`,
  height: "32.5rem",
  margin: "0.7rem 0.7rem 0.7rem 0",
  position: "relative",
  overflowY: "auto",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    width: "0.5rem",
    height: "0.1rem",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: colors.black[7],
    borderRadius: "0.3rem",
  },
});

export const VideoCardMedia = () => ({
  backgroundColor: colors.grey[3],
  padding: "6rem 1rem",
  position: "relative",
  width: "100%",
});

export const PlayButtonBox = () => ({
  position: "absolute",
  top: "25%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1,
});

export const PlayButton = () => ({
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  borderRadius: "50%",
  minWidth: 0,
  height: 0,
  padding: 0,
  margin: 0,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.8)",
  },
});

export const ToggleButton = () => ({
  color: colors.portalColor[2],
  backgroundColor: "transparent",
  boxShadow: "none",
  textTransform: "none",
  width: "100%",
  "&:hover": {
    backgroundColor: "transparent",
    borderColor: "none",
    boxShadow: "none",
  },
  "&:active": {
    boxShadow: "none",
    backgroundColor: "transparent",
    borderColor: "none",
  },
  "&:focus": {
    boxShadow: "none",
  },
});

export const CardActionsStyle = () => ({
  paddingRight: "1.5rem",
});

export const ActionIconBox = () => ({
  marginLeft: "auto",
  paddingTop: "0.2rem",
});

export const EditIcon = {
  marginRight: "1.5rem",
};

export const VideoDescriptionCollapsed = (showFullDescription: boolean) => ({
  minHeight: "3rem",
  lineHeight: "1.5rem",
  overflow: "hidden",
  display: "-webkit-box",
  whiteSpace: "normal",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: showFullDescription ? "none" : 3,
  textOverflow: "ellipsis",
  wordWrap: "break-word",
  overflowWrap: "break-word",
});

export const TextfieldStyle = {
  marginTop: "0.5rem",
  "& input::placeholder": {
    color: colors.portalColor[1],
    fontWeight: fontWeight[2],
    fontSize: "0.9rem",
    opacity: 0.9,
  },
  "& .MuiOutlinedInput-root": {
    height: "2.4rem",
    "&.Mui-focused fieldset": {
      boxShadow: `0.0625rem 0.0625rem 0.625rem ${colors.dropShadow[3]}`,
    },
    "&.Mui-error fieldset": {
      boxShadow: `0.0625rem 0.0625rem 0.625rem ${colors.dropShadow[3]}`,
    },
  },
  "& .MuiInputBase-input": {
    "&.MuiOutlinedInput-input": {
      color: colors.portalColor[1],
      fontWeight: fontWeight[2],
      fontSize: "1rem",
    },
  },
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: colors.grey[4],
  },
};

export const BackLink = styled(Link)(() => ({
  color: colors.arrowIcon[1],
  textDecoration: "none",
  "&:hover": {
    color: colors.portalColor[22],
    textDecoration: "underline",
  },
}));

export const BoxOuterContainer = () => ({
  padding: "1.8rem 2rem 2rem 1rem",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minHeight: "75vh",
  maxHeight: "75vh",
  overflowY: "auto",
});

export const BoxInnerContainer = () => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

export const VideoPlayerBox = () => ({
  width: "80%",
  margin: "2rem 0",
});

export const VideoContainer = () => ({
  borderRadius: "0.7rem",
  overflow: "hidden",
  width: "80%",
  height: "auto",
});

export const CustomTextarea = (error: FieldError | undefined) => ({
  marginTop: "0.5rem",
  borderRadius: "0.25rem",
  color: colors.portalColor[1],
  fontWeight: fontWeight[2],
  fontSize: "1rem",
  padding: "0.5rem 0.8rem",
  fontFamily: "DM Sans",
  "&:hover": {
    border: `1px solid ${colors.dropShadow[3]}`,
  },
  border: error ? "1px solid red" : "1px solid rgba(0, 0, 0, 0.23)",
  boxShadow: error
    ? `0.0625rem 0.0625rem 0.625rem ${colors.dropShadow[3]}`
    : "none",
});

export const VideoTextfield = {
  marginTop: "0.5rem",
  "& .MuiOutlinedInput-root": {
    height: "2.4rem",
    "&.Mui-focused fieldset": {
      boxShadow: `0.0625rem 0.0625rem 0.625rem ${colors.dropShadow[3]}`,
    },
    "&.Mui-error fieldset": {
      boxShadow: `0.0625rem 0.0625rem 0.625rem ${colors.dropShadow[3]}`,
    },
  },
  "& .MuiInputBase-input": {
    "&.MuiOutlinedInput-input": {
      color: colors.portalColor[1],
      fontWeight: fontWeight[2],
      fontSize: "1rem",
      height: "1.8rem",
      padding: "0.7rem 0.5rem 0",
      "&:placeholder": {
        color: colors.portalColor[1],
        fontWeight: fontWeight[2],
        fontSize: "0.9rem",
        opacity: 0.9,
      },
    },
  },
};

export const TimeFormControl = () => ({
  width: "30%",
});

export const DescriptionFormControl = () => ({
  width: "70%",
});

export const RemoveButton = () => ({
  marginLeft: 0,
  marginRight: 0,
  marginTop: "0.5rem",
  backgroundColor: colors.portalColor[14],
});

export const AddBookmarkButton = () => ({
  marginLeft: 0,
  marginTop: "0.6rem",
});

export const WelcomeBox = () => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "50vh",
});

export const BoxCenter = () => ({
  textAlign: "center",
});

export const GridContainer = () => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minHeight: "75vh",
  maxHeight: "75vh",
  overflowY: "auto",
});

export const LongText = () => ({
  fontSize: "1.2rem",
  wordWrap: "break-word" as React.CSSProperties["wordWrap"],
  overflowWrap: "break-word" as React.CSSProperties["overflowWrap"],
});

export const FaRegCirclePlayIcon = styled(FaRegCirclePlay as React.ComponentType)(() => ({
  marginRight: "0.75rem",
  fontSize: "2rem",
}));

export const FaPenToSquareIcon = styled(FaPenToSquare as React.ComponentType)(() => ({
  marginRight: "1.5rem",
  fontSize: "1.2rem"
}));

export const FaRegTrashCanIcon = styled(FaRegTrashCan as React.ComponentType)(() => ({
  color: colors.red[100],
  fontSize: "1.2rem"
}));
